import PageBannerSlot from '@/components/common/banner/PageBannerSlot';
import { usePageBanners } from '@/components/common/banner/usePageBanners';
import { ListPageShell, ListTabRow, ListTabs, ListTypeTab, ListFilterRow, ListBody, ListCreateButton, useListHeaderHeight } from '@/components/common/organism/ListPageLayout';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useRecoilValue } from 'recoil';
import { api } from '@/api/repository/axios';
import { useListRestore, useScrollRestore } from '@/common/listRestore';
import { FILTER_TOUCHED_STATE, useSavedFilterOnEntry } from '@/common/savedListFilter';
import { formatDateOnly } from '@/components/common/datePicker/pickerUtils';
import { userAtom } from '@/state/loginState';
import { designSystem } from '@/styles/theme';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import Pagination from '@/components/common/elements/Pagination';
import RecruitmentGrid from '@/components/domain/recruitment/RecruitmentGrid';
import PremiumRecruitmentSection from '@/components/domain/recruitment/PremiumRecruitmentSection';
import type { RecruitmentListData } from '@/components/domain/recruitment/types';
import { CheckToggle, ListControlRow, SortSelect, filterTokens, listControlGap, useIsMobileWidth } from '@/components/common/filter';
import RecruitmentFilterBar from './components/RecruitmentFilterBar';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import type { RecruitmentFilterOptions, RecruitmentPanelKind } from './components/RecruitmentFilterBar';
import {
    RECRUITMENT_TABS,
    clearRecruitmentConditions,
    hasRecruitmentCondition,
    isPlainRecruitmentEntry,
    parseRecruitmentListQuery,
    parseSavedRecruitmentQuery,
    recruitmentQueryKey,
    savedRecruitmentForEntry,
    toRecruitmentApiRequest,
    writeRecruitmentListQuery,
    RECRUITMENT_SORT_DEFAULT,
    RECRUITMENT_SORT_OPTIONS,
    SAME_DAY_PAY_TIMING,
    toggleSameDayPay,
} from './recruitmentListQuery';
import type { RecruitmentApiRequest, RecruitmentListQuery, RecruitmentSort, RecruitmentTab } from './recruitmentListQuery';
import { loadRegionData } from './recruitmentRegions';
import type { RegionData } from './recruitmentRegions';
import { resolveStations } from './recruitmentStations';
import { useTodayRecruitments } from './useTodayRecruitments';
import { track } from '@/common/analytics';
import { useListKey, useUiScope } from '@/common/useUiScope';
import { UI_SCREEN } from '@/common/uiEventNames';

/**
 * 채용 목록 (세미나 목록과 같은 구성 — 2026-09-30 개편).
 *
 * 탭(지역별 · 지하철역별 — 홈 바로가기가 각각으로 보낸다) → 조건 칩 한 줄(칩을 누르면 폰은 아래 시트, PC 는 칩 아래 팝업) → 목록.
 * 조건은 모두 주소 쿼리에 있다(recruitmentListQuery.ts) — 주소가 바뀌면 이전 목록은 바로 비우고 로딩 표시 뒤 새 응답만 그린다.
 * 거르기(복지·근무일수 포함)와 정렬(모집 중 먼저, 마감 공고는 뒤)은 서버가 한다. 늦게 온 옛 응답은 요청 번호로 버린다.
 * 폰은 무한 스크롤, PC 는 페이지 번호. 공고를 보고 뒤로 오면 보던 목록·페이지·스크롤을 되살린다(common/listRestore).
 * [공고 등록]·떠 있는 + 버튼은 치과 회원에게만.
 */

const PAGE_SIZE = 32; // PC 한 줄 4개 × 8줄

interface Banner {
    id: number;
    title: string;
    imageUrl: string;
    mobileImageUrl?: string;
    linkUrl?: string;
}

interface Banners {
    RECRUITMENT_TOP: Banner[];
    RECRUITMENT_BOTTOM: Banner[];
}

/** /recruitments/init 에서 받는 선택지·배너 — 한 번 받으면 기억해 두고(뒤로 왔을 때 칩 글자가 깜박이지 않게) 다시 받아 갱신한다 */
interface InitData {
    options: RecruitmentFilterOptions;
    banners: Banners;
}

const EMPTY_OPTIONS: RecruitmentFilterOptions = { jobTasks: [], recruitmentTypes: [], careers: [], welfares: [] };
const EMPTY_BANNERS: Banners = { RECRUITMENT_TOP: [], RECRUITMENT_BOTTOM: [] };
let initCache: InitData | null = null;

/** 뒤로가기로 돌아왔을 때 되살릴 목록 상태 (common/listRestore 참고) */
interface RecruitmentListSnapshot {
    recruitments: RecruitmentListData[];
    page: number;
    total: number;
    hasMore: boolean;
    /** 이 목록을 받은 조건 — 주소의 조건과 같을 때만 되살린다 */
    queryKey: string;
    /** 폰 목록(이어 받기)인지 — PC 페이지 목록과 섞지 않는다 */
    mobile: boolean;
}

const Container = ListPageShell;
const TabBar = styled(ListTabs)`width: 100%;`;
const TabButton = ListTypeTab;
const ChipRow = ListFilterRow;

const ListArea = ListBody;

const TodaySection = styled.section`
    min-width: 0;
    margin: 16px 0 12px;
`;

/**
 * PC 카드 그리드(MUI Grid spacing)는 위로 16px 당겨 올라온다 — 그 만큼 안쪽 여백을 둔 상자에 넣어
 * 위의 검색어 줄·배너를 덮지 않게 한다(덮으면 [검색어 지우기]를 누를 수 없었다).
 */
/** 폰 조작 줄 왼쪽 — 바로 켜고 끄는 조건 묶음(급구·당일 지급). 오른쪽 정렬과 겹치지 않게 줄어들 수 있다 */
const ControlToggles = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const GridBox = styled.div<{ afterBanner?: boolean }>`
    /* 배너 아래 간격에는 목록의 시작 여백도 포함한다(16+8=24). */
    padding-top: ${p => p.afterBanner ? '8px' : '16px'};

    @media (max-width: 768px) {
        padding-top: 0;
        /* 모바일 배너 8px + 목록 4px = 조작 줄 아래와 같은 12px. */
        ${p => p.afterBanner && '[data-recruitment-mobile-list] { padding-top: 4px; }'}
    }
`;

/** 검색어 줄(헤더 검색으로 들어왔을 때만) */
const ListMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;
    margin: 12px 0 0;
    font-size: 14px;
    color: ${filterTokens.color.textSub};

    @media (max-width: 768px) {
        margin: 12px 0 -4px;
    }
`;

const MetaText = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TextButton = styled.button`
    flex-shrink: 0;
    margin-left: auto;
    padding: 4px 2px;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 13px;
    color: ${filterTokens.color.textMuted};
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
`;

const EmptyState = styled.div`
    padding: 64px 16px;
    text-align: center;

    p {
        margin: 0 0 20px;
        font-size: 16px;
        color: ${filterTokens.color.textSub};
        word-break: keep-all;
    }
`;

const SecondaryButton = styled.button`
    height: 44px;
    padding: 0 20px;
    border: 1px solid ${filterTokens.color.border};
    border-radius: 14px;
    background: ${filterTokens.color.surface};
    color: ${filterTokens.color.textSub};
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
`;



/** 치과 회원 폰 + 버튼 — 하단 탭 위 */
const FloatingButton = styled.button`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        bottom: calc(90px + env(safe-area-inset-bottom));
        right: 12px;
        width: 56px;
        height: 56px;
        border: none;
        border-radius: 50%;
        background: ${designSystem.colors.primary};
        color: #fff;
        font-size: 28px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        cursor: pointer;
        z-index: ${designSystem.zIndex.sticky};

        &:active {
            transform: scale(0.95);
        }
    }
`;

/**
 * 목록 조건 기록 값 — id·코드만(검색어 원문·근무 날짜는 싣지 않고 개수에만 넣는다). docs/unified-log-plan.md 2.2 filter_apply
 */
const recruitmentFilterProps = (query: RecruitmentListQuery): Record<string, string | number | string[]> => {
    const tags: string[] = [...query.welfares, ...query.workdays, ...query.periods, ...(query.emergency ? ['EMERGENCY'] : [])];
    const props: Record<string, string | number | string[]> = {
        sort: query.sort,
        filterCount: query.districts.length + query.stations.length + query.jobTasks.length + query.recruitmentTypes.length
            + query.careers.length + query.payTimings.length + query.workDates.length + tags.length,
    };
    if (query.districts.length > 0) props.region = query.districts;
    if (query.stations.length > 0) props.station = query.stations;
    if (query.recruitmentTypes.length > 0) props.workType = query.recruitmentTypes;
    if (query.jobTasks.length > 0) props.position = query.jobTasks;
    if (query.careers.length > 0) props.career = query.careers;
    if (query.payTimings.length > 0) props.pay = query.payTimings;
    if (tags.length > 0) props.tag = tags;
    props.category = query.tab;
    return props;
};

const toOptions = (list: Array<{ description: string; type: string }> | undefined) =>
    (list ?? []).map((item: { description: string; type: string }) => ({ name: item.description, value: item.type }));

const RecruitmentList: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // 조건은 주소가 원본 — 칩·시트·탭은 주소를 바꾸고, 주소가 바뀌면 목록을 다시 받는다
    const query: RecruitmentListQuery = useMemo(() => parseRecruitmentListQuery(searchParams), [searchParams]);
    const queryKey: string = recruitmentQueryKey(query);
    const queryRef = useRef<RecruitmentListQuery>(query);
    queryRef.current = query;
    const user = useRecoilValue(userAtom);
    const isClinicUser: boolean = user?.role?.includes('ROLE_CLINIC') ?? false;
    const isMobile: boolean = useIsMobileWidth();
    const headerHeight: number = useListHeaderHeight(true);

    // 공고를 보고 뒤로 왔을 때: 다시 받지 않고 보던 목록·페이지·스크롤 위치를 그대로 되살린다(조건·화면 종류가 같을 때만)
    const { restored, save } = useListRestore<RecruitmentListSnapshot>('recruitments:region');
    const initialRestoreRef = useRef<RecruitmentListSnapshot | null | undefined>(undefined);
    if (initialRestoreRef.current === undefined) {
        initialRestoreRef.current = restored && restored.queryKey === queryKey && restored.mobile === isMobile ? restored : null;
    }
    const restoredSnapshot: RecruitmentListSnapshot | null = initialRestoreRef.current;
    useScrollRestore(restoredSnapshot !== null);
    /** 지금 화면의 조건+화면 종류 — 목록이 이 조건으로 받은 것일 때만 그린다 */
    const viewKey: string = `${queryKey}|${isMobile ? 'mobile' : 'pc'}`;
    const viewKeyRef = useRef<string>(viewKey);
    viewKeyRef.current = viewKey;

    const [recruitments, setRecruitments] = useState<RecruitmentListData[]>(restoredSnapshot?.recruitments ?? []);
    const [page, setPage] = useState<number>(restoredSnapshot?.page ?? 0);
    const [total, setTotal] = useState<number>(restoredSnapshot?.total ?? 0);
    const [hasMore, setHasMore] = useState<boolean>(restoredSnapshot?.hasMore ?? false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [loadingFirst, setLoadingFirst] = useState<boolean>(restoredSnapshot === null);
    /** 목록 요청 실패 — first: 첫 페이지(빈 목록 대신 "불러오지 못했어요"), more: 이어 받기(자동으로 다시 부르지 않고 버튼으로) */
    const [loadError, setLoadError] = useState<'first' | 'more' | null>(null);
    /**
     * 지금 들고 있는 목록을 받은 조건(viewKey). 조건을 바꾸면 새 응답이 올 때까지 이전 조건의 목록은 그리지 않는다
     * (세미나 목록에서 고친 깜박임과 같은 규칙). 되살린 목록은 지금 조건과 같으므로 바로 그린다
     */
    const [listKey, setListKey] = useState<string | null>(restoredSnapshot !== null ? viewKey : null);
    /** 마지막으로 보낸 첫 페이지 요청 번호 — 조건이 바뀐 뒤 늦게 온 옛 응답은 버린다 */
    const requestSeqRef = useRef<number>(0);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    /** 폰에서 조건·탭을 바꾼 직후 — 새 목록을 받을 때 맨 위로 올린다 */
    const scrollTopPendingRef = useRef<boolean>(false);
    /** PC 페이지 버튼으로 이동한 요청만 새 DOM 적용 후 최상단을 보장한다. */
    const pcPageScrollRef = useRef<{ viewKey: string; page: number; requestSeq: number } | null>(null);

    // 노출·클릭 기록 — 조건·화면 종류가 바뀌면 새 목록 표시. "오늘의 채용"은 따로 센다
    const trackListKey: string = useListKey(`recruitments:${viewKey}`);
    const listAreaRef = useUiScope({ screen: UI_SCREEN.RECRUIT_LIST, section: 'list', listKey: trackListKey });
    const todayRef = useUiScope({ screen: UI_SCREEN.RECRUIT_LIST, section: 'today', listKey: trackListKey });

    const [initData, setInitData] = useState<InitData | null>(initCache);
    const [regionData, setRegionData] = useState<RegionData | null>(null);
    /** 주소의 역 이름을 찾았을 때 칩 글자를 다시 그리려고 올리는 번호 */
    const [, setStationNamesVersion] = useState<number>(0);
    /** 홈 "직무별" 바로가기(`tab=job`)로 들어오면 직무 창을 한 번 연다 */
    const [autoOpen, setAutoOpen] = useState<RecruitmentPanelKind | null>(null);

    // 선택지·배너 — 목록 요청과 따로(기다리지 않는다)
    useEffect(() => {
        api.recruitmentComponents.getRecruitmentPageInit(0)
            .then((response) => {
                const result = response.result;
                const next: InitData = {
                    options: {
                        jobTasks: toOptions(result.meta.jobTasks),
                        recruitmentTypes: toOptions(result.meta.recruitmentType),
                        careers: toOptions(result.meta.careerType),
                        welfares: (result.welfareChips ?? []).map((chip: { label: string; value: string }) => ({ name: chip.label, value: chip.value })),
                    },
                    banners: { ...EMPTY_BANNERS, ...(result.banners ?? {}) },
                };
                initCache = next;
                setInitData(next);
            })
            .catch((error: unknown) => console.error('채용 목록 선택지 조회 실패:', error));
        loadRegionData()
            .then(setRegionData)
            .catch((error: unknown) => console.error('지역 목록 조회 실패:', error));
    }, []);

    // 홈 "직무별" 바로가기 — 옛 `tab` 값은 주소에서 치우고(방문 기록 칸을 늘리지 않게) 직무 창을 연다
    useEffect(() => {
        if (searchParams.get('tab') !== 'job') return;
        setSearchParams((prev: URLSearchParams) => {
            const params: URLSearchParams = new URLSearchParams(prev);
            params.delete('tab');
            return params;
            // 직무 창을 바로 여는 진입 — 저장한 조건을 켜지 않는다(창에서 고르는 것이 먼저)
        }, { replace: true, state: FILTER_TOUCHED_STATE });
        if (restoredSnapshot === null) setAutoOpen('job');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleAutoOpened = useCallback((): void => setAutoOpen(null), []);

    // 주소의 역 id → 칩 글자(이름·노선)
    const stationIdsKey: string = query.stations.join(',');
    useEffect(() => {
        if (query.stations.length === 0) return;
        let alive: boolean = true;
        resolveStations(query.stations).then(() => { if (alive) setStationNamesVersion((value: number) => value + 1); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stationIdsKey]);

    /** 한 페이지 요청 — 검색어가 있으면 검색 API(다른 조건과 함께 못 쓴다), 아니면 지역·지하철 목록 */
    const fetchPage = async (current: RecruitmentListQuery, pageNum: number): Promise<{ list: RecruitmentListData[]; total: number }> => {
        const offset: number = pageNum * PAGE_SIZE;
        const response = current.search
            ? await api.recruitments.search(current.search, PAGE_SIZE, offset)
            : await (async () => {
                const request: RecruitmentApiRequest = toRecruitmentApiRequest(current);
                return api.recruitments.listPage(request.kind, request.params, PAGE_SIZE, offset);
            })();
        if (response.code !== 0) throw new Error(response.message || '채용 목록 조회 실패');
        const result = response.result as { list?: RecruitmentListData[]; total?: number };
        return { list: result.list ?? [], total: Number(result.total ?? 0) };
    };

    /** 현재 조건(주소)으로 특정 페이지 조회. append=true 면 이어 붙인다(폰 무한 스크롤) */
    const loadRecruitments = async (pageNum: number, append: boolean): Promise<void> => {
        const current: RecruitmentListQuery = queryRef.current;
        const requestKey: string = viewKeyRef.current;
        const seq: number = append ? requestSeqRef.current : ++requestSeqRef.current;
        if (append) setLoadingMore(true);
        else setLoadingFirst(true);
        setLoadError(null);
        try {
            const { list, total: totalCount } = await fetchPage(current, pageNum);
            if (seq !== requestSeqRef.current || requestKey !== viewKeyRef.current) return; // 응답 전에 조건이 바뀜
            setHasMore(list.length >= PAGE_SIZE && (pageNum + 1) * PAGE_SIZE < totalCount);
            // 이어 붙일 때는 이미 있는 id 를 뺀다(페이지 경계가 밀려도 같은 카드가 두 번 안 보이게)
            setRecruitments((prev: RecruitmentListData[]) => {
                if (!append) return list;
                const seen: Set<number> = new Set(prev.map((item: RecruitmentListData) => item.id));
                return [...prev, ...list.filter((item: RecruitmentListData) => !seen.has(item.id))];
            });
            setTotal(totalCount);
            setListKey(requestKey);
        } catch (error) {
            console.error('채용 목록 조회 오류:', error);
            if (seq !== requestSeqRef.current || requestKey !== viewKeyRef.current) return;
            if (append) {
                // 이 페이지는 못 받았으니 페이지 번호를 되돌린다 — [다시 불러오기]가 같은 페이지를 다시 부른다
                setPage(pageNum - 1);
                setLoadError('more');
            } else {
                setRecruitments([]);
                setTotal(0);
                setHasMore(false);
                setListKey(requestKey);
                setLoadError('first');
            }
        } finally {
            if (append) setLoadingMore(false);
            else if (seq === requestSeqRef.current) setLoadingFirst(false);
        }
    };

    // 조건(주소)·화면 종류가 바뀌면 처음부터 다시 받는다. 되살린 목록이면 첫 번은 건너뛴다
    const skipFirstLoadRef = useRef<boolean>(restoredSnapshot !== null);
    useEffect(() => {
        if (skipFirstLoadRef.current) {
            skipFirstLoadRef.current = false;
            return;
        }
        // 이전 조건의 목록은 비우고 첫 페이지 로딩 표시부터 — 새 응답으로만 채운다
        setRecruitments([]);
        setTotal(0);
        setHasMore(false);
        setPage(0);
        void loadRecruitments(0, false);
        if (scrollTopPendingRef.current) {
            scrollTopPendingRef.current = false;
            // 시트가 닫히며 몸통 고정을 풀 때 제자리로 돌리므로 한 프레임 뒤에도 한 번 더 올린다
            window.scrollTo(0, 0);
            window.requestAnimationFrame(() => window.scrollTo(0, 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey, isMobile]);

    // 폰 무한 스크롤: 센티넬이 보이면 다음 페이지를 이어 붙인다
    useEffect(() => {
        if (!isMobile) return undefined;
        const element: HTMLDivElement | null = loadMoreRef.current;
        if (!element) return undefined;
        const observer: IntersectionObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            if (entries[0].isIntersecting && !loadingMore && !loadingFirst && hasMore && loadError === null) {
                const next: number = page + 1;
                setPage(next);
                void loadRecruitments(next, true);
            }
        }, { rootMargin: '200px' });
        observer.observe(element);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, loadingMore, loadingFirst, hasMore, page, listKey, loadError]);

    useLayoutEffect(() => {
        const pending = pcPageScrollRef.current;
        if (!pending) return;
        if (isMobile || pending.viewKey !== viewKey || pending.requestSeq !== requestSeqRef.current) {
            pcPageScrollRef.current = null;
            return;
        }
        if (loadingFirst || listKey !== viewKey || page !== pending.page) return;
        pcPageScrollRef.current = null;
        // WebKit은 짧은 마지막 페이지로 높이가 줄면 진행 중인 smooth 이동을 중단한다.
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [isMobile, viewKey, listKey, loadingFirst, page, recruitments]);

    // 완료된 목록만 기억한다. 이어 받는 중에는 페이지 번호만 앞서 있어 저장하면 다음 공고를 건너뛸 수 있다.
    useEffect(() => {
        if (listKey !== viewKey || loadingFirst || loadingMore) return;
        save({ recruitments, page, total, hasMore, queryKey, mobile: isMobile });
    }, [recruitments, page, total, hasMore, queryKey, isMobile, save, listKey, viewKey, loadingFirst, loadingMore]);

    /**
     * 조건을 주소에 쓴다(되돌리기 칸을 늘리지 않게 replace) — 주소가 바뀌면 위 효과가 목록을 다시 받는다
     * @param fromSaved 저장한 조건을 켠 것(사람이 고른 것이 아님) — 목록 조건 기록을 남기지 않는다
     */
    const applyQuery = (next: RecruitmentListQuery, fromSaved = false): void => {
        if (recruitmentQueryKey(next) === queryKey && next.districts.join() === query.districts.join() && next.stations.join() === query.stations.join()) return;
        if (!fromSaved) track.filterApply({ screen: UI_SCREEN.RECRUIT_LIST, listKey: trackListKey }, recruitmentFilterProps(next));
        if (isMobile) scrollTopPendingRef.current = true;
        setSearchParams((prev: URLSearchParams) => {
            const params: URLSearchParams = writeRecruitmentListQuery(prev, next);
            return params.toString() === prev.toString() ? prev : params;
        }, { replace: true, state: FILTER_TOUCHED_STATE });
    };

    // 조건 없이 들어오면 저장한 조건을 켠다(정렬은 지금 것 그대로, 지난 근무 날짜는 뺀다). 지역·역은 둘 다 되살린다
    const savedEntry = useSavedFilterOnEntry('recruitment', isPlainRecruitmentEntry(searchParams) && restoredSnapshot === null, (savedQuery: string) => {
        applyQuery(savedRecruitmentForEntry(queryRef.current, parseSavedRecruitmentQuery(savedQuery, formatDateOnly(new Date()))), true);
    });
    const today = useTodayRecruitments(query, !savedEntry.waiting);

    const handleTabChange = (tab: RecruitmentTab): void => {
        if (tab === query.tab) return;
        applyQuery({ ...query, tab });
    };

    // 탭 방향키 이동(←/→) — WAI-ARIA 탭 패턴
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next: number = (index + 1) % RECRUITMENT_TABS.length;
        tabRefs.current[next]?.focus();
        handleTabChange(RECRUITMENT_TABS[next].key);
    };

    // 저장한 조건을 읽는 중이면 아직 그리지 않는다 — 기본 목록이 잠깐 보였다가 바뀌지 않게
    const listReady: boolean = listKey === viewKey && !savedEntry.waiting;
    const shown: RecruitmentListData[] = listReady ? recruitments : [];
    const shownToday: RecruitmentListData[] = today.list;
    const showFirstLoading: boolean = loadingFirst || !listReady;
    const conditionApplied: boolean = hasRecruitmentCondition(query);
    // 상단·하단 배너는 공용 자리(PageBannerSlot)가 이 화면 위치 + 모든 페이지 배너를 한 번에 받는다 — 여기서는 상단 유무만 본다(같은 요청 공유)
    const pageBanners = usePageBanners();

    return (
        <Container data-list-page="recruitment">
                <ListTabRow style={{ top: headerHeight }}>
                <TabBar role="tablist" aria-label="채용 공고 찾기">
                    {RECRUITMENT_TABS.map((tab, index: number) => (
                        <TabButton
                            key={tab.key}
                            ref={(element: HTMLButtonElement | null) => { tabRefs.current[index] = element; }}
                            id={`recruitment-tab-${tab.key}`}
                            type="button"
                            role="tab"
                            aria-selected={query.tab === tab.key}
                            aria-controls="recruitment-tab-panel"
                            tabIndex={query.tab === tab.key ? 0 : -1}
                            active={query.tab === tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => handleTabKeyDown(event, index)}
                        >
                            {tab.label}
                        </TabButton>
                    ))}
                </TabBar>
                {isClinicUser && <ListCreateButton type="button" onClick={() => navigate('/recruitments/new')}>공고 등록</ListCreateButton>}
                </ListTabRow>
                <ChipRow>
                    <RecruitmentFilterBar
                        query={query}
                        options={initData?.options ?? EMPTY_OPTIONS}
                        regionData={regionData}
                        isMobile={isMobile}
                        onApply={applyQuery}
                        sortChip={isMobile || query.search ? undefined : (
                            <SortSelect
                                options={RECRUITMENT_SORT_OPTIONS}
                                value={query.sort}
                                defaultValue={RECRUITMENT_SORT_DEFAULT}
                                onChange={(sort: RecruitmentSort) => applyQuery({ ...query, sort })}
                            />
                        )}
                        autoOpen={autoOpen}
                        onAutoOpened={handleAutoOpened}
                    />
                </ChipRow>

            <ListArea ref={listAreaRef} role="tabpanel" id="recruitment-tab-panel" aria-labelledby={`recruitment-tab-${query.tab}`}>
                {/* 폰: 급구·정렬은 필터 아래 한 줄. 급구를 바꾸면 다른 조건처럼 검색어를 푼다. 검색 중에는 정렬을 숨긴다(검색 API 가 따로) */}
                {isMobile && (
                    // 아래에 오는 배너·검색 안내·카드 목록은 모두 위 여백이 12px
                    <ListControlRow style={listControlGap(12)}>
                        {/* 급구·당일 지급 — 바로 켜고 끄는 조건 둘을 왼쪽에 나란히(PC 는 칩 줄의 [급구][당일 지급]) */}
                        <ControlToggles>
                            <CheckToggle
                                label="급구만 보기"
                                checked={query.emergency}
                                onColor={FORM_COLOR.danger}
                                onChange={(emergency: boolean) => applyQuery({ ...query, emergency, search: '' })}
                            />
                            <CheckToggle
                                label="당일 지급만"
                                checked={query.payTimings.includes(SAME_DAY_PAY_TIMING)}
                                onChange={() => applyQuery({ ...toggleSameDayPay(query), search: '' })}
                            />
                        </ControlToggles>
                        {!query.search && (
                            <SortSelect
                                options={RECRUITMENT_SORT_OPTIONS}
                                value={query.sort}
                                defaultValue={RECRUITMENT_SORT_DEFAULT}
                                onChange={(sort: RecruitmentSort) => applyQuery({ ...query, sort })}
                            />
                        )}
                    </ListControlRow>
                )}
                <PageBannerSlot placement="top" variant="list" />

                {query.search && (
                    <ListMeta aria-live="polite">
                        <MetaText>‘{query.search}’ 검색 결과{listReady && !loadingFirst ? ` ${total.toLocaleString()}건` : ''}</MetaText>
                        <TextButton type="button" onClick={() => applyQuery({ ...query, search: '' })}>검색어 지우기</TextButton>
                    </ListMeta>
                )}

                {shownToday.length > 0 && (
                    <TodaySection ref={todayRef} data-recruitment-section="today">
                        <PremiumRecruitmentSection
                            data={shownToday}
                            onCardClick={(id: number) => navigate(`/recruitments/${id}`)}
                            resetKey={today.queryKey}
                            desktopGridAligned
                        />
                    </TodaySection>
                )}

                {shown.length === 0 ? (
                    showFirstLoading ? (
                        <LoadingSpinner placement="page" minHeight="40vh" />
                    ) : loadError === 'first' ? (
                        <EmptyState role="alert">
                            <p>공고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</p>
                            <SecondaryButton type="button" onClick={() => { setPage(0); void loadRecruitments(0, false); }}>
                                다시 시도
                            </SecondaryButton>
                        </EmptyState>
                    ) : (
                        <EmptyState>
                            <p>{conditionApplied ? '조건에 맞는 공고가 없어요.' : '등록된 공고가 없어요.'}</p>
                            {conditionApplied && (
                                <SecondaryButton type="button" onClick={() => applyQuery(clearRecruitmentConditions(query))}>
                                    조건 모두 풀기
                                </SecondaryButton>
                            )}
                        </EmptyState>
                    )
                ) : (
                    <>
                        <GridBox afterBanner={pageBanners.top.length > 0 && !query.search && shownToday.length === 0}>
                            <RecruitmentGrid data={shown} layout="list" />
                        </GridBox>
                        {/* PC: 페이지 번호 / 폰: 무한 스크롤 */}
                        {!isMobile && total > PAGE_SIZE && (
                            <Pagination
                                currentPage={page}
                                totalPages={Math.ceil(total / PAGE_SIZE)}
                                onPageChange={(next: number) => {
                                    pcPageScrollRef.current = { viewKey, page: next, requestSeq: requestSeqRef.current + 1 };
                                    setPage(next);
                                    void loadRecruitments(next, false);
                                    window.scrollTo({ top: 0, behavior: 'instant' });
                                }}
                            />
                        )}
                        {isMobile && hasMore && loadError === null && <div ref={loadMoreRef} style={{ height: 1 }} />}
                        {loadingMore && <LoadingSpinner />}
                        {loadError === 'more' && (
                            <EmptyState role="alert" style={{ padding: '24px 16px' }}>
                                <p>다음 공고를 불러오지 못했어요.</p>
                                <SecondaryButton type="button" onClick={() => { const next: number = page + 1; setPage(next); void loadRecruitments(next, true); }}>
                                    다시 불러오기
                                </SecondaryButton>
                            </EmptyState>
                        )}
                        <PageBannerSlot placement="bottom" variant="list" />
                    </>
                )}
            </ListArea>

            {isClinicUser && (
                <FloatingButton type="button" aria-label="공고 등록" onClick={() => navigate('/recruitments/new')}>
                    <AddRoundedIcon fontSize="inherit" />
                </FloatingButton>
            )}

        </Container>
    );
};

export default RecruitmentList;
